import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    students: 0,
    teachers: 0,
    classes: 0,
    pending_grades: 0,
    pending_absences: 0,
    total_payments: 0,
    pending_payments: 0,
  });
  const [recentStudents, setRecentStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const statsRes = await fetch('/api/admin/dashboard', {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!statsRes.ok) throw new Error('Erreur de chargement');
      const statsData = await statsRes.json();
      setStats(statsData.stats || {});

      try {
        const studentsRes = await fetch('/api/students', {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (studentsRes.ok) {
          const studentsData = await studentsRes.json();
          const list = Array.isArray(studentsData) ? studentsData : (studentsData.data || []);
          setRecentStudents(list.slice(0, 5));
        }
      } catch (e) {
        console.log('Pas d\'élèves récents');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value) => {
    if (!value) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
  };

  // 🔑 Mettre la première lettre en majuscule
  const firstName = user?.name 
    ? user.name.split(' ')[0].charAt(0).toUpperCase() + user.name.split(' ')[0].slice(1).toLowerCase()
    : 'Admin';

  return (
    <div className="p-6 lg:p-8 bg-white min-h-screen">

      {/* ═══════════ BANNIÈRE HAUT ═══════════ */}
      <div className="relative rounded-2xl overflow-hidden mb-6 h-48 lg:h-56 bg-[#0A0A0A]">

        <img
          src="/images/banner-admin.png"
          alt="Admin"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent"></div>

        <div className="relative z-10 p-6 lg:p-8 h-full flex flex-col justify-center max-w-xl">
          <h1 className="text-white text-3xl lg:text-4xl font-bold">
            Bonjour {firstName} 👋
          </h1>
          <p className="text-white/95 text-base lg:text-lg mt-3">
            Bienvenue sur votre espace d'administration.
          </p>
          <p className="text-white/80 text-sm lg:text-base mt-1">
            Gérez votre établissement en toute simplicité.
          </p>
        </div>
      </div>

      {loading && (
        <div className="text-center py-20 text-gray-500 text-lg">Chargement...</div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-base rounded-xl px-5 py-4 mb-6">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ═══════════ 4 CARTES STATS ═══════════ */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={<UsersIcon className="w-8 h-8 text-white" />}
              bg="bg-[#3B82F6]"
              value={stats.students || 0}
              label="Élèves"
              linkTo="/admin/students"
            />
            <StatCard
              icon={<UserIcon className="w-8 h-8 text-white" />}
              bg="bg-[#1E40AF]"
              value={stats.teachers || 0}
              label="Professeurs"
              linkTo="/admin/teachers"
            />
            <StatCard
              icon={<CapIcon className="w-8 h-8 text-white" />}
              bg="bg-[#22C55E]"
              value={stats.classes || 0}
              label="Classes"
              linkTo="/admin/classes"
            />
            <StatCard
              icon={<BookIcon className="w-8 h-8 text-white" />}
              bg="bg-[#A855F7]"
              value={stats.courses || stats.classes || 0}
              label="Cours"
              linkTo="/admin/courses"
            />
          </div>

          {/* ═══════════ 2 COLONNES ═══════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

            {/* Élèves récents */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-bold text-[#0A0A0A] text-xl">Élèves récents</h3>
                <Link
                  to="/admin/students"
                  className="text-[#F39C12] hover:text-[#E67E22] text-base font-medium"
                >
                  Voir tout →
                </Link>
              </div>

              {recentStudents.length === 0 ? (
                <p className="text-gray-500 text-center py-8 text-base">
                  Aucun élève inscrit pour le moment
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-sm text-gray-500 border-b border-gray-100">
                        <th className="text-left py-3 font-medium">Nom et prénom</th>
                        <th className="text-left py-3 font-medium">Classe</th>
                        <th className="text-left py-3 font-medium">Statut</th>
                        <th className="text-right py-3 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentStudents.map((s) => (
                        <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F39C12] to-[#E67E22] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                                {(s.name || s.user?.name || 'E').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                              <span className="font-medium text-base text-[#0A0A0A]">
                                {s.name || s.user?.name || 'Élève'}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 text-base text-gray-600">
                            {s.class_name || s.class?.name || '-'}
                          </td>
                          <td className="py-3">
                            <span className="text-sm px-3 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                              Actif
                            </span>
                          </td>
                          <td className="py-3 text-right">
                            <Link to={`/admin/students`} className="text-[#F39C12] text-base">
                              →
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Raccourcis */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-bold text-[#0A0A0A] text-xl">Raccourcis</h3>
              </div>

              <div className="space-y-3">
                <ShortcutButton icon={<UserIcon className="w-5 h-5 text-white" />} label="Ajouter un élève" to="/admin/students" />
                <ShortcutButton icon={<BookIcon className="w-5 h-5 text-white" />} label="Créer un cours" to="/admin/courses" />
                <ShortcutButton icon={<CapIcon className="w-5 h-5 text-white" />} label="Gérer les classes" to="/admin/classes" />
                <ShortcutButton icon={<ChartIcon className="w-5 h-5 text-white" />} label="Voir les bulletins" to="/admin/bulletins" />
              </div>

              <div className="mt-6 bg-orange-50 rounded-xl p-4 flex items-start gap-3">
                <span className="text-2xl">💡</span>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Une bonne gestion aujourd'hui, des réussites demain !
                </p>
              </div>
            </div>
          </div>

          {/* ═══════════ 3 CARTES INFO ═══════════ */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <InfoCard
              icon={<PenIcon className="w-7 h-7 text-[#F39C12]" />}
              title="Notes en attente"
              value={stats.pending_grades || 0}
              linkTo="/admin/notes-validation"
            />
            <InfoCard
              icon={<WarningIcon className="w-7 h-7 text-[#F39C12]" />}
              title="Absences à justifier"
              value={stats.pending_absences || 0}
              linkTo="/admin/absences"
            />
            <InfoCard
              icon={<MoneyIcon className="w-7 h-7 text-[#F39C12]" />}
              title="Total payé"
              value={formatMoney(stats.total_payments)}
              linkTo="/admin/payments"
            />
          </div>

          {/* ═══════════ BANNIÈRE BAS ═══════════ */}
          <div className="bg-orange-50 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#F39C12] flex items-center justify-center flex-shrink-0 text-white">
              <ShieldIcon className="w-7 h-7" />
            </div>
            <div>
              <p className="font-bold text-[#0A0A0A] text-base">SamaEcole</p>
              <p className="text-gray-600 text-sm">
                Une école connectée pour un meilleur avenir.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// COMPOSANTS
// ═══════════════════════════════════════════════════════════════════

function StatCard({ icon, bg, value, label, linkTo }) {
  return (
    <Link
      to={linkTo}
      className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-lg hover:border-[#F39C12]/30 transition-all duration-300 group"
    >
      <div className={`w-14 h-14 rounded-xl ${bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        {icon}
      </div>
      <p className="text-4xl font-extrabold text-[#0A0A0A]">{value}</p>
      <p className="text-base text-gray-500 mt-2">{label}</p>
    </Link>
  );
}

function InfoCard({ icon, title, value, linkTo }) {
  return (
    <Link
      to={linkTo}
      className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition flex items-center justify-between group"
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-orange-100 flex items-center justify-center">
          {icon}
        </div>
        <div>
          <p className="text-gray-500 text-base">{title}</p>
          <p className="text-3xl font-extrabold text-[#0A0A0A] mt-1">{value}</p>
        </div>
      </div>
      <span className="text-gray-400 group-hover:text-[#F39C12] group-hover:translate-x-1 transition text-lg">
        →
      </span>
    </Link>
  );
}

function ShortcutButton({ icon, label, to }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 p-3.5 rounded-xl bg-gray-50 hover:bg-[#FFF3E0] transition group"
    >
      <div className="w-11 h-11 rounded-lg bg-[#F39C12] flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <span className="text-base font-medium text-[#0A0A0A] flex-1">{label}</span>
      <span className="text-gray-400 group-hover:text-[#F39C12] transition text-base">→</span>
    </Link>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ICÔNES SVG
// ═══════════════════════════════════════════════════════════════════

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const UserIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
  </svg>
);

const CapIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M22 10L12 5 2 10l10 5 10-5z" />
    <path d="M6 12v5c0 1 2 3 6 3s6-2 6-3v-5" />
  </svg>
);

const BookIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const ChartIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M3 3v18h18" />
    <path d="M7 12l4 4 8-8" />
  </svg>
);

const PenIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const WarningIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const MoneyIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v12M9 9h4.5a2.5 2.5 0 0 1 0 5H9M9 14h5" />
  </svg>
);

const ShieldIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export default AdminDashboard;